import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { inr } from "../lib/format";

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty, subtotal, lineKey, count } = useCart();
  const { user, openLogin } = useAuth();
  const navigate = useNavigate();

  const goCheckout = () => {
    setIsOpen(false);
    if (!user) {
      openLogin("/checkout");
      return;
    }
    navigate("/checkout");
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setIsOpen(false)}
      />
      {/* Panel */}
      <aside
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-[#FFF8F0] z-[70] shadow-2xl transition-transform duration-300 flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-5 h-[70px] border-b border-[#EADBCB]">
          <h3 className="font-display text-xl font-bold text-[#3A2417] flex items-center gap-2">
            <ShoppingBag size={20} className="text-[#D46A8A]" /> Your Cart ({count})
          </h3>
          <button onClick={() => setIsOpen(false)} className="w-10 h-10 grid place-items-center rounded-full hover:bg-[#F6E7DA]">
            <X size={22} className="text-[#6B4226]" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-center px-8">
            <div>
              <div className="w-20 h-20 rounded-full bg-[#F6E7DA] grid place-items-center mx-auto mb-4">
                <ShoppingBag size={32} className="text-[#D46A8A]" />
              </div>
              <p className="text-[#6B4226] font-medium">Your cart is empty</p>
              <p className="text-sm text-[#B07A5C] mt-1">Add some delicious cakes to get started!</p>
              <button onClick={() => setIsOpen(false)} className="mt-5 px-6 h-11 rounded-full bg-[#6B4226] text-white text-sm hover:bg-[#5a3620] transition-colors">
                Continue Shopping
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map((i) => {
                const key = lineKey(i);
                return (
                  <div key={key} className="flex gap-3 bg-white rounded-2xl p-3 bakery-shadow">
                    <img src={i.image} alt={i.name} className="w-20 h-20 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-[#3A2417] line-clamp-1">{i.name}</h4>
                      <p className="text-xs text-[#B07A5C]">{i.flavor} · {i.weight}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2 bg-[#F6E7DA] rounded-full px-1">
                          <button onClick={() => updateQty(key, i.qty - 1)} className="w-7 h-7 grid place-items-center text-[#6B4226]"><Minus size={14} /></button>
                          <span className="text-sm w-5 text-center">{i.qty}</span>
                          <button onClick={() => updateQty(key, i.qty + 1)} className="w-7 h-7 grid place-items-center text-[#6B4226]"><Plus size={14} /></button>
                        </div>
                        <span className="font-semibold text-sm text-[#3A2417]">{inr(i.price * i.qty)}</span>
                      </div>
                    </div>
                    <button onClick={() => removeItem(key)} className="text-[#B07A5C] hover:text-[#D46A8A] self-start"><Trash2 size={18} /></button>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-[#EADBCB] p-5 space-y-4 bg-[#FFF8F0]">
              <div className="flex items-center justify-between">
                <span className="text-[#6B4226]">Subtotal</span>
                <span className="font-display text-xl font-bold text-[#3A2417]">{inr(subtotal)}</span>
              </div>
              <p className="text-xs text-[#B07A5C]">Shipping & taxes calculated at checkout.</p>
              <button onClick={goCheckout} className="w-full h-12 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors" data-testid="cart-proceed-checkout-btn">
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
